import { observer } from "mobx-react-lite";
import { appRoutes } from "@/app/router/appRoutes";
import { FC, useCallback, useEffect } from "react";
import { Navigate, useLocation, useSearchParams } from "react-router-dom";
import { usePublicAppStore } from "@/store";
import { AuthCardLogIn } from "@/components/auth";
import { EXTENSION_CLOSE_WINDOW_COOKIE_NAME } from "@/domains/extension";
import Cookies from "js-cookie";
import { AuthLayout } from "@/components/auth/layout/auth-layout";
import { localStorageModule } from "@/modules/local-storage";
import { readIMessageConnectionClaimToken } from "@/modules/mem-agent/imessage-connection-claim-url";
import { markIMessageClaimFlowSession } from "@/modules/mem-agent/imessage-claim-flow-session";

export const LogInPage: FC = observer(() => {
  const { publicStore } = usePublicAppStore();
  const location = useLocation();
  const [searchParams] = useSearchParams();
  const claimToken = readIMessageConnectionClaimToken({ searchParams });
  const notesPath = appRoutes.notesList({}).path;
  const imessageOnboardingCompletePath = appRoutes.imessageOnboardingComplete({}).path;

  const preserveIMessageClaimRedirect = useCallback(async () => {
    if (claimToken === null) {
      return;
    }

    markIMessageClaimFlowSession();

    // Claim-bearing login links must survive OAuth/signup redirects before AppStore can claim them.
    await localStorageModule.writePendingRedirectPath({
      pathname: imessageOnboardingCompletePath,
      search: location.search,
      hash: location.hash,
    });
  }, [claimToken, imessageOnboardingCompletePath, location.hash, location.search]);

  useEffect(() => {
    const referrer = searchParams.get("referrer");

    if (referrer === "chrome-extension") {
      // Set cookie to indicate window should close after authentication
      // This is used when the Chrome extension opens a popup window for auth.
      // After successful login, the window will detect this cookie and auto-close,
      // returning control to the extension.
      // Not used in standard flow (it wont have referrer query)
      Cookies.set(EXTENSION_CLOSE_WINDOW_COOKIE_NAME, "true", {
        path: "/",
        secure: true,
        sameSite: "strict",
        // Short expiry - only needed during auth flow
        expires: 1 / 24, // 1 hour
      });
    }
  }, [searchParams]);

  useEffect(() => {
    void preserveIMessageClaimRedirect();
  }, [preserveIMessageClaimRedirect]);

  if (publicStore.auth.isAuthenticated) {
    if (claimToken !== null) {
      return (
        <Navigate
          to={{
            pathname: imessageOnboardingCompletePath,
            search: location.search,
            hash: location.hash,
          }}
          replace
        />
      );
    }

    return <Navigate to={notesPath} />;
  }

  return (
    <AuthLayout>
      <AuthCardLogIn prepareLogIn={preserveIMessageClaimRedirect} />
    </AuthLayout>
  );
});
